import { Component, OnDestroy, OnInit } from '@angular/core';
import { ConfigManagerService } from '../../shared/services/config-manager.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { select, Store } from '@ngrx/store';
import { RootState } from '../../store/store.state';
import { ValidateJWTError, ValidateJWTSuccess } from '../store/auth.actions';
import type { Observable } from 'rxjs';
import { selectAuthState } from '../store/auth.store';
import { AuthState } from '../store/auth.state';
import { AuthErrorCode } from '../../../../common/models/auth-api.interface';

@Component({
    standalone: false,
    selector: 'q-auth-frame',
    template: `
        <iframe
            style="width:0px; height:0px; position:absolute;"
            class="invisible"
            [src]="sanitizedFrameUrl"
            *ngIf="(authState$ | async)?.ui?.showFrame"
        ></iframe>
    `
})
export class AuthFrameComponent implements OnInit, OnDestroy {
    private readonly frameUrl: string;
    public readonly sanitizedFrameUrl: SafeResourceUrl;
    public authState$: Observable<AuthState>;
    private readonly AUTH_TIMEOUT_DELAY = 10;
    private authTimeout: ReturnType<typeof setTimeout> | null = null;

    private readonly boundHandleFrameMessage = (ev: MessageEvent): void => {
        this.handleFrameMessage(ev);
    };

    constructor(
        public authManagerService: ConfigManagerService,
        private sanitizer: DomSanitizer,
        private store: Store<RootState>
    ) {
        this.frameUrl = `${this.authManagerService.ssoUrl}/embed/`;
        this.sanitizedFrameUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            this.frameUrl
        );

        this.authState$ = this.store.pipe(select(selectAuthState));
    }

    ngOnInit(): void {
        window.addEventListener('message', this.boundHandleFrameMessage);
        this.authTimeout = setTimeout(() => {
            // If timeout is reached before a message is received then treat user as unauthenticated
            this.store.dispatch(
                new ValidateJWTError(AuthErrorCode.UNAUTHENTICATED)
            );
        }, this.AUTH_TIMEOUT_DELAY * 1000);
    }

    handleFrameMessage(ev: MessageEvent): void {
        if (ev && ev.origin === this.authManagerService.ssoUrl) {
            if (this.authTimeout) {
                clearTimeout(this.authTimeout);
                this.authTimeout = null;
            }

            if (!ev.data?.jwt || !ev.data?.claims) {
                this.store.dispatch(
                    new ValidateJWTError(AuthErrorCode.UNAUTHENTICATED)
                );
                return;
            }

            this.store.dispatch(new ValidateJWTSuccess(ev.data));
        }
    }

    ngOnDestroy(): void {
        window.removeEventListener('message', this.boundHandleFrameMessage);

        if (this.authTimeout) {
            clearTimeout(this.authTimeout);
            this.authTimeout = null;
        }
    }
}
